import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import {
  formatDateTime,
  IconPaths,
  IconSize,
  KTSVG,
  QUERIES,
  showError,
} from '../../../../../_metronic/helpers'
import { useT } from '../../../../../_metronic/i18n/Metronici18n'
import {
  Button,
  InfoAlert,
  LoadingIndicator,
  PageHeading,
  StatusDot,
  TableCard,
} from '../../../../components'
import { CLASSE_LINK_OUTLINE } from '../../protocolos-triagem/core/classeLinkOutline'
import { getNotaVersaoUsuarioDisponivel, marcarNotaVersaoLida } from '../core/_requests'
import { NotaVersaoCorpo } from '../components/NotaVersaoCorpo'

export const NovidadeDetalhe = () => {
  const { t } = useT()
  const { id } = useParams()
  const idNota = Number(id)
  const idValido = Number.isInteger(idNota) && idNota > 0
  const queryClient = useQueryClient()
  const [marcando, setMarcando] = useState(false)

  const {
    data: nota,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    [QUERIES.NOTAS_VERSAO_HISTORICO, 'detalhe', idNota],
    () => getNotaVersaoUsuarioDisponivel(idNota),
    { enabled: idValido, retry: false, refetchOnWindowFocus: false }
  )

  const marcarLida = async () => {
    setMarcando(true)
    try {
      await marcarNotaVersaoLida(idNota)
      // O backend também marca como lida a notificação da nota: o sino precisa recarregar.
      await Promise.all([
        queryClient.invalidateQueries(QUERIES.NOTAS_VERSAO_HISTORICO),
        queryClient.invalidateQueries(QUERIES.NOTAS_VERSAO_VIGENTE),
        queryClient.invalidateQueries([QUERIES.NOTIFICACOES_LIST]),
        queryClient.invalidateQueries([QUERIES.NOTIFICACOES_NAO_LIDAS_COUNT]),
      ])
    } catch (erro) {
      showError(erro)
    } finally {
      setMarcando(false)
    }
  }

  // Mesmo padrão das páginas de detalhe: título no PageHeading e o caminho de volta nas ações.
  const voltar = (
    <Link to='/apps/novidades' className={CLASSE_LINK_OUTLINE}>
      <span className='me-2 d-flex align-items-center' aria-hidden='true'>
        <KTSVG path={IconPaths.ArrowLeft} size={IconSize.ExtraSmall} />
      </span>
      {t('notas_versao.historico.voltar')}
    </Link>
  )

  return (
    <>
      <PageHeading title={nota?.titulo ?? t('menu.novidades')} actions={voltar} />
      <TableCard>
        <div className='card-body d-flex flex-column gap-5'>
          {isLoading && <LoadingIndicator message={t('notas_versao.carregando')} />}
          {(!idValido || isError) && (
            <InfoAlert
              variant='danger'
              role='alert'
              action={
                idValido ? (
                  <Button variant='secondary-outline' onClick={() => refetch()}>
                    {t('notas_versao.historico.tentar_novamente')}
                  </Button>
                ) : undefined
              }
            >
              {t('notas_versao.historico.erro')}
            </InfoAlert>
          )}
          {nota === null && (
            <InfoAlert variant='info' role='status' title={t('notas_versao.indisponivel.titulo')}>
              {t('notas_versao.indisponivel.descricao')}
            </InfoAlert>
          )}
          {nota && (
            <>
              <NotaVersaoCorpo
                versao={nota.versao}
                dtPublicacao={nota.dtPublicacao}
                conteudo={nota.conteudo}
                selo={
                  <StatusDot
                    label={
                      nota.situacao === 'LIDA' && nota.dtLeitura
                        ? t('notas_versao.lida_em', { data: formatDateTime(nota.dtLeitura) })
                        : t(`notas_versao.leitura.${nota.situacao}`)
                    }
                    tone={nota.situacao === 'LIDA' ? 'neutral' : 'primary'}
                  />
                }
              />
              {nota.situacao !== 'LIDA' && (
                <div className='d-flex justify-content-end'>
                  <Button className='btn-primary' onClick={marcarLida} disabled={marcando}>
                    {marcando ? <LoadingIndicator /> : t('notas_versao.historico.marcar_lida')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </TableCard>
    </>
  )
}
